import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import citiesData from "../data/cities-and-postalcode-by-district-min.json";
import { RootState } from "../store/store";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../firebase-config"; // Adjust the path as necessary
import { clearCart } from "../store/cartSlice"; // Import the clearCart action
import { runTransaction, doc } from "firebase/firestore";
import emailjs from "emailjs-com"; // Import EmailJS
import emailConfig from "../email-config";
import companyDetailsConfig from "../company-details-config";

const Checkout: React.FC = () => {
  const dispatch = useDispatch(); // Initialize dispatch
  const userId = useSelector((state: RootState) => state.user.userId);
  const [formData, setFormData] = useState({
    address_line_1: "",
    address_line_2: "",
    district: "",
    city: "",
    contact_no_1: "",
    contact_no_2: "",
    email: "",
    name: "",
    postal_code: "",
    payment_method: "Cash On Delivery",
    items_price: 0,
  });
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBankDetails, setShowBankDetails] = useState(false); // State to handle the visibility of bank details
  const [cities, setCities] = useState<
    { city: string; code: string; district: string }[]
  >([]);
  const [loading, setLoading] = useState(false); // Loading state for spinner
  const [giftWish, setGiftWish] = useState("");

  // Get total price from Redux
  const items = useSelector((state: RootState) => state.cart.items);
  // Compute total price (accounting for variations)
  // Compute total price (accounting for variations and sales)
  const totalPrice = items.reduce((total, item) => {
    const { selectedVariant } = item;
    const unitPrice = item.isSaleActive
      ? selectedVariant.salePrice // Use salePrice if sale is active
      : selectedVariant.unitPrice; // Otherwise, use regular price
    return total + unitPrice * selectedVariant.quantity;
  }, 0);

  // Check if 5 Cloth Diaper Pack is in the cart
  const hasFivePack = items.some(
    (item) => item.name === "5 Regular Cloth Diapers with 2 Inserts"
  );

  const hasGiftPack = items.some(
    (item) => item.name === "Cozy Bud Gift Bloom"
  );

  // const hasCutePack = items.some(
  //   (item) => item.name === "3 Cloth Diapers - Cute Pack"
  // );

  const hasMegaPack = items.some(
    (item) => item.name === "8 Cloth Diapers - Mega Pack"
  );

  const hasHomies = items.some(
    (item) => item.name === "Cozy Bud Homies"
  );

  const totalClothDiapers = items.reduce((total, item) => {
    const isClothDiaper = item.name?.toLowerCase().includes('cloth diaper') ||
      item.name?.toLowerCase().includes('cloth diaper');
    return isClothDiaper ? total + (item.selectedVariant.quantity || 1) : total;
  }, 0);

  // Set courier charges based on the presence of 5-pack
  let COURIER_CHARGES = 420;

  if (hasFivePack || hasGiftPack || hasMegaPack || totalClothDiapers >= 5 || hasHomies) {
    COURIER_CHARGES = 300;
  }
  // else if (hasCutePack || totalClothDiapers == 3) {
  //   COURIER_CHARGES = 300;
  // }
  // else if (totalClothDiapers == 4) {
  //   COURIER_CHARGES = 200;
  // }

  //const COURIER_CHARGES = 400; // Define courier charges

  // Define the promotional message
  let promoMessage = "";
  if (hasFivePack || hasMegaPack || totalClothDiapers >= 5) {
    promoMessage = "🎉 You got 25% off from delivery for purchasing more than 4 diapers!";
  }
  if (hasHomies) {
    promoMessage = "🎉 You got 25% off from delivery for purchasing Cozy Bud Homies!";
  }
  // if (hasCutePack || totalClothDiapers == 3) {
  //   promoMessage = "🎉 You got 25% off from delivery for purchasing 3 diapers!";
  // }
  // if (totalClothDiapers == 4) {
  //   promoMessage = "🎉 You got 50% off from delivery for purchasing 4 diapers!";
  // }
  if (hasGiftPack) {
    promoMessage += "\n🎀 Since you're purchasing a gift pack, please provide recipient's delivery details and complete payment via bank transfer.";
  }

  // Calculate final price
  const finalPrice = totalPrice + COURIER_CHARGES; // Define courier charges
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const allCities = Object.entries(citiesData).flatMap(
      ([district, cityList]) => cityList.map((city) => ({ ...city, district }))
    );
    setCities(allCities);
  }, []);

  const handleCityChange = (
    event: React.SyntheticEvent,
    value: { city: string; code: string; district: string } | null
  ) => {
    if (value) {
      setFormData({
        ...formData,
        city: value.city,
        postal_code: value.code,
        district: value.district,
      });
    } else {
      setFormData({ ...formData, city: "", postal_code: "", district: "" });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePaymentMethodChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedPaymentMethod = e.target.value;
    setFormData({
      ...formData,
      payment_method: selectedPaymentMethod,
    });

    // Show bank details only when "Bank Payment" is selected
    setShowBankDetails(selectedPaymentMethod === "Bank Payment");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); // Set loading state to true when submitting

    // Check for mandatory fields
    if (
      !formData.name ||
      !formData.address_line_1 ||
      !formData.city ||
      !formData.contact_no_1
    ) {
      setError("Please fill in all mandatory fields.");
      return;
    }

    const customerRef = collection(db, "customers");
    let customerId;
    let customerDoc;

    try {
      // First, check if the customer exists by email
      const customerByEmailSnapshot = await getDocs(
        query(customerRef, where("email", "==", formData.email))
      );

      if (!customerByEmailSnapshot.empty) {
        // Customer exists, get their ID
        customerDoc = customerByEmailSnapshot.docs[0];
        customerId = customerDoc.id;
        // Check if contact number has changed
        if (
          customerDoc.data().contact_no_1 !== formData.contact_no_1 ||
          customerDoc.data().name !== formData.name
        ) {
          // Update both fields if either contact number or name is different
          await updateDoc(customerDoc.ref, {
            contact_no_1: formData.contact_no_1,
            contact_no_2: formData.contact_no_2,
            name: formData.name,
          });
        }
      } else {
        // If email doesn't exist, check by contact_no_1
        const customerByContactSnapshot = await getDocs(
          query(customerRef, where("contact_no_1", "==", formData.contact_no_1))
        );

        if (!customerByContactSnapshot.empty) {
          // Customer exists by contact number, get their ID
          //  customerId = customerByContactSnapshot.docs[0].id;
          customerDoc = customerByContactSnapshot.docs[0];
          // Ensure the other details match before assuming it's the same person
          const existingData = customerDoc.data();
          if (
            existingData.name === formData.name &&
            existingData.contact_no_2 === formData.contact_no_2
          ) {
            // It's the same person, reuse their ID
            customerId = customerDoc.id;
          } else {
            // Details do not match exactly → Create a new customer
            const newCustomerData = {
              name: formData.name,
              email: formData.email,
              contact_no_1: formData.contact_no_1,
              contact_no_2: formData.contact_no_2,
            };

            const newCustomerDoc = await addDoc(customerRef, newCustomerData);
            customerId = newCustomerDoc.id;
          }

        } else {
          // Create new customer
          const customerData = {
            name: formData.name,
            email: formData.email,
            contact_no_1: formData.contact_no_1,
            contact_no_2: formData.contact_no_2,
          };

          const customerDoc = await addDoc(customerRef, customerData);
          customerId = customerDoc.id;
        }
      }

      // Check for existing address
      const addressRef = collection(db, "addresses");
      const addressSnapshot = await getDocs(
        query(
          addressRef,
          where("customer_id", "==", customerId),
          where("postal_code", "==", formData.postal_code),
          where("city", "==", formData.city),
          where("district", "==", formData.district)
        )
      );

      let addressId;

      if (!addressSnapshot.empty) {
        // Address exists, get its ID
        addressId = addressSnapshot.docs[0].id;
      } else {
        // Create new address
        const addressData = {
          customer_id: customerId,
          address_line_1: formData.address_line_1,
          address_line_2: formData.address_line_2,
          city: formData.city,
          district: formData.district,
          postal_code: formData.postal_code,
        };

        const addressDoc = await addDoc(addressRef, addressData);
        addressId = addressDoc.id;
      }

      // Generate tracking number
      const generateTrackingNumber = () => {
        const now = new Date();
        const yy = now.getFullYear().toString().slice(-2); // Last two digits of the year
        const mm = String(now.getMonth() + 1).padStart(2, '0'); // Month (01-12)
        const dd = String(now.getDate()).padStart(2, '0'); // Day (01-31)
        const hh = String(now.getHours()).padStart(2, '0'); // Hours (00-23)
        const min = String(now.getMinutes()).padStart(2, '0'); // Minutes (00-59)
        const ss = String(now.getSeconds()).padStart(2, '0'); // Seconds (00-59)

        return `CB${yy}${mm}${dd}${hh}${min}${ss}`;
      };

      const trackingNumber = generateTrackingNumber();

      // Create order summary
      const orderSummaryRef = collection(db, "orderSummaries");

      // Prepare order summary data
      const orderSummaryData = {
        courier_charge: COURIER_CHARGES, // Add your courier charge here
        courier_id: "ProntoLanka", // Set your courier ID here
        customer_id: customerId,
        address_id: addressId,
        payment_method: formData.payment_method, // User selected payment method
        order_date: serverTimestamp(),
        total_price: totalPrice + COURIER_CHARGES, // Add total price and courier charges
        tracking_number: trackingNumber,
        status: "In Progress",
        gift_wish: hasGiftPack ? giftWish : "", // Include gift wish if a gift pack is in the cart
        items: items.map((item) => ({
          item_id: item.id,
          item_name: item.name,
          variant_id: item.selectedVariant.id,
          color: item.selectedVariant.color,
          size: item.selectedVariant.size,
          quantity: item.selectedVariant.quantity,
          unit_price: (item.isSaleActive) ? item.selectedVariant.salePrice : item.selectedVariant.unitPrice,
          total_price:
            (item.isSaleActive) ? item.selectedVariant.salePrice * item.selectedVariant.quantity : item.selectedVariant.unitPrice * item.selectedVariant.quantity,
        })),
      };

      console.log("Order data before save:", JSON.stringify(orderSummaryData, null, 2));


      const cleanedOrderData = JSON.parse(JSON.stringify(orderSummaryData, (key, value) => {
        return value === undefined ? null : value;
      }));

      const dataToSave = {
        ...cleanedOrderData,
        order_date: serverTimestamp(),
      };
      const orderSummaryDoc = await addDoc(orderSummaryRef, dataToSave);
      const orderSummaryId = orderSummaryDoc.id;

      await Promise.all(
        items.map(async (item) => {
          // Update item quantity in inventory
          const itemRef = doc(db, "items", item.id);
          try {
            await runTransaction(db, async (transaction) => {
              const itemSnapshot = await transaction.get(itemRef);
              if (itemSnapshot.exists()) {
                const itemData = itemSnapshot.data();
                const variants = itemData.variant || [];
                // Find the specific variant to update
                const variantIndex = variants.findIndex(
                  (variant: any) =>
                    variant.color === item.selectedVariant.color &&
                    variant.material === item.selectedVariant.material
                );

                if (variantIndex !== -1) {
                  // Update the quantity of the selected variant
                  variants[variantIndex].quantity -=
                    item.selectedVariant.quantity;

                  transaction.update(itemRef, { variant: variants });
                }
              }
            });
          } catch (error) {
            setLoading(false);
            console.error(`Error updating item ${item.id}:`, error);
            // Handle the error as needed (e.g., show a message to the user or log the error)
            throw new Error(`Failed to update item ${item.id}`);
          }
        })
      );

      // Clear the cart
      dispatch(clearCart());

      const pluralize = (itemName: string, quantity: number) => {
        // Handle specific cases where pluralization isn't just adding an "s"
        const exceptions: { [key: string]: string } = {
          Nappy: "Nappies",
          Pack: "Packs"
        };

        let result = itemName;
        for (const [key, value] of Object.entries(exceptions)) {
          const regex = new RegExp(`\\b${key}\\b`, "i"); // Match exact word (case-insensitive)
          if (regex.test(itemName)) {
            result = result.replace(regex, quantity > 1 ? value : key);
          }
        }

        // If no exceptions found, default to adding 's' to the item name
        return quantity > 1 && !result.includes("s") ? `${result}s` : result;
      };

      let contactNo =
        formData.contact_no_2 === ""
          ? formData.contact_no_1
          : formData.contact_no_1 + ", " + formData.contact_no_2;
      let c_address =
        formData.address_line_2 === ""
          ? formData.address_line_1 +
          ", " +
          formData.city +
          " (" +
          formData.postal_code +
          "), " +
          formData.district
          : formData.address_line_1 +
          ", " +
          formData.address_line_2 +
          ", " +
          formData.city +
          " (" +
          formData.postal_code +
          "), " +
          formData.district;
      const orderDetails = items
        .map(
          (item) =>
            `${item.selectedVariant.quantity} ${item.selectedVariant.color} ${pluralize(
              item.name,
              item.selectedVariant.quantity
            )}, Unit Price: LKR ${item.isSaleActive ? item.selectedVariant.salePrice.toFixed(2) : item.selectedVariant.unitPrice.toFixed(2)}`
        )
        .join("\n");


      // Prepare the email data
      const emailData = {
        customer_name: formData.name,
        customer_email: formData.email,
        tracking_Number: trackingNumber,
        shipping_fee: COURIER_CHARGES.toFixed(2),
        total_payment: (totalPrice + COURIER_CHARGES).toFixed(2),
        customer_address: c_address,
        contact_no: contactNo,
        payment_method: formData.payment_method,
        order_details: orderDetails, // You can pass actual order details here from your cart
      };

      // Send the email via EmailJS
      try {
        if (formData.email) {
          await emailjs.send(
            emailConfig.serviceId,
            emailConfig.templateId,
            emailData, // Pass the email data here
            emailConfig.userId
          );
        }
        setOrderSuccess(true);
      } catch (error) {
        setLoading(false);
        console.error("Error sending email:", error);
        setError("There was an error processing your order. Please try again.");
      }
      // Navigate to confirmation screen upon success
      setLoading(false);
      navigate("/confirmation", { state: { trackingNumber } });
    } catch (dbError) {
      setLoading(false);
      console.error("Database error:", dbError);
      setError(
        "There was an error processing your order. Please try again later."
      ); // Set the error state
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ padding: 4, backgroundColor: "rgba(255, 255, 255, 0.4)" }}
    >
      <Typography variant="h4" gutterBottom>
        Delivery Details
      </Typography>
      {error && <Typography color="error">{error}</Typography>}{" "}
      {/* Display error message here */}
      {/* Name */}
      <TextField
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        fullWidth
        required
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {/* Email */}
      <TextField
        label="Email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        fullWidth
        required
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {/* Address Line 1 */}
      <TextField
        label="Address Line 1"
        name="address_line_1"
        value={formData.address_line_1}
        onChange={handleChange}
        fullWidth
        required
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {/* Address Line 2 */}
      <TextField
        label="Address Line 2"
        name="address_line_2"
        value={formData.address_line_2}
        onChange={handleChange}
        fullWidth
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {/* City Autocomplete */}
      <Autocomplete
        options={cities}
        getOptionLabel={(option) => option.city}
        onChange={handleCityChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="City"
            required
            sx={{ marginBottom: 2 }}
            InputLabelProps={{ sx: { color: "#57b7fb" } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} style={{ color: "#000", backgroundColor: "#fff" }}>
            {option.city} ({option.code})
          </li>
        )}
      />
      {/* Postal Code */}
      <TextField
        label="Postal Code"
        name="postal_code"
        type="number"
        value={formData.postal_code}
        onChange={handleChange}
        fullWidth
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
        inputProps={{ readOnly: true }}
      />
      {/* District (Auto-populated) */}
      <TextField
        label="District"
        name="district"
        value={formData.district}
        fullWidth
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
        InputProps={{
          readOnly: true,
        }}
      />
      {/* Contact No 1 */}
      <TextField
        label="Contact No 1"
        name="contact_no_1"
        type="text"
        value={formData.contact_no_1}
        onChange={handleChange}
        fullWidth
        required
        inputProps={{
          maxLength: 10, // Limit to 10 digits
        }}
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {/* Contact No 2 */}
      <TextField
        label="Contact No 2"
        name="contact_no_2"
        type="text"
        value={formData.contact_no_2}
        onChange={handleChange}
        fullWidth
        inputProps={{
          maxLength: 10, // Limit to 10 digits
        }}
        sx={{ marginBottom: 2 }}
        InputLabelProps={{ sx: { color: "#57b7fb" } }}
      />
      {hasGiftPack && (
        <TextField
          label="Gift Wish Message"
          name="gift_wish"
          type="text"
          value={giftWish}
          onChange={(e) => setGiftWish(e.target.value)}
          fullWidth
          placeholder="Enter your wish for the gift pack"
          sx={{ marginBottom: 2 }}
          InputLabelProps={{ sx: { color: "#57b7fb" } }}
        />
      )}
      {/* Display total price with courier charges */}
      <Typography variant="h3" sx={{ marginTop: 2, fontSize: { xs: "0.9rem", sm: "1.0rem", md: "1.3rem" } }}>
        Total Price: LKR {finalPrice.toFixed(2)} (includes LKR {COURIER_CHARGES.toFixed(2)}{" "}
        courier charges)
      </Typography>

      {promoMessage && (
        <Typography
          variant="h4"
          sx={{
            marginTop: 1,
            color: "#A27EA8",
            fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1.1rem" },
          }}
        >
          {promoMessage}
        </Typography>
      )}

      <br />
      {/* Payment Method */}
      <Typography variant="h6" gutterBottom>
        Payment Method
      </Typography>
      <RadioGroup
        name="payment_method"
        value={formData.payment_method}
        onChange={handlePaymentMethodChange}
      >
        <FormControlLabel
          value="Cash on Delivery"
          control={<Radio />}
          label="Cash on Delivery"
        />
        <FormControlLabel
          value="Bank Payment"
          control={<Radio />}
          label="Bank Payment"
        />
      </RadioGroup>
      {/* Conditionally render bank details if Bank Payment is selected */}
      {showBankDetails && (
        <Box
          sx={{
            marginTop: 2,
            padding: 2,
            backgroundColor: "#57b7fb",
            borderRadius: 2,
          }}
        >
          <Typography variant="h2">Bank Details</Typography>
          <Typography>Account Name: {companyDetailsConfig.name}</Typography>
          <Typography>Account Number: {companyDetailsConfig.bankAccountNo}</Typography>
          <Typography>Bank Name: {companyDetailsConfig.bankName}</Typography>
          <Typography>Branch: {companyDetailsConfig.branchName}</Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            Please do the payment of LKR {finalPrice.toFixed(2)} and send the
            transaction receipt via WhatsApp at {companyDetailsConfig.mobile} or email at {companyDetailsConfig.email} to dispatch the order.
          </Typography>
        </Box>
      )}
      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{ marginTop: 2, color: "#fff", fontWeight: "600" }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : "Checkout"}
      </Button>
      {/* Back to Cart Button */}
      <Button
        variant="contained"
        color="secondary"
        sx={{ marginTop: 2, marginLeft: 2, color: "#fff", fontWeight: "600" }}
        onClick={() => navigate("/shoppingcart")} // Navigate to Cart page
      >
        Back to Cart
      </Button>
    </Box>
  );
};

export default Checkout;
